import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCurrency, formatDate } from '@/helpers/billing'
import { BillingInvoice } from '@/types/billing'
import { CircleDollarSign, RotateCcw } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Pagination } from '@/components/custom/Pagination/Pagination'

interface InvoiceHistoryProps {
  invoices?: BillingInvoice[]
  onRefresh: () => void
  isLoading: boolean
}

export const InvoiceHistory: React.FC<InvoiceHistoryProps> = ({
  invoices,
  onRefresh,
  isLoading
}) => {
  const [limit, setLimit] = useState(5)
  const [offset, setOffset] = useState(0)

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      paid: 'bg-green-100 text-green-800 hover:text-background',
      open: 'bg-yellow-100 text-yellow-800 hover:text-background',
      draft: 'bg-gray-100 text-gray-800 hover:text-background',
      void: 'bg-red-100 text-red-800 hover:text-background',
      uncollectible: 'bg-red-100 text-red-800 hover:text-background'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const renderSkeleton = () => (
    <tbody>
      {Array.from({ length: 3 }).map((_, idx) => (
        <tr key={idx} className="border-b border-gray-100">
          <td className="py-3 pr-4"><Skeleton className="h-4 w-24" /></td>
          <td className="py-3 pr-4"><Skeleton className="h-4 w-28" /></td>
          <td className="py-3 pr-4"><Skeleton className="h-4 w-16" /></td>
          <td className="py-3 pr-4"><Skeleton className="h-5 w-14 rounded-full" /></td>
          <td className="py-3 text-right"><Skeleton className="h-7 w-24 ml-auto" /></td>
        </tr>
      ))}
    </tbody>
  )

  const renderEmpty = () => (
    <tbody>
      <tr>
        <td colSpan={5} className="py-10 text-center text-sm text-gray-500">
          No invoices found
        </td>
      </tr>
    </tbody>
  )

  const paginatedInvoices = invoices?.slice(offset, offset + limit)

  return (
    <div className="px-6 py-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">Invoice History</h2>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RotateCcw className="w-3.5 h-3.5 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="pb-2.5 text-left font-semibold text-gray-500 pr-4">Date</th>
              <th className="pb-2.5 text-left font-semibold text-gray-500 pr-4">Invoice</th>
              <th className="pb-2.5 text-left font-semibold text-gray-500 pr-4">Amount</th>
              <th className="pb-2.5 text-left font-semibold text-gray-500 pr-4">Status</th>
              <th className="pb-2.5 text-right font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          {isLoading
            ? renderSkeleton()
            : (invoices?.length ?? 0) > 0
              ? (
                <tbody className="divide-y divide-gray-100">
                  {paginatedInvoices?.map((invoice) => (
                    <tr key={invoice.id} className={`transition-colors hover:bg-gray-50 ${invoice.status === 'open' ? 'bg-yellow-50/40' : ''}`}>
                      <td className="py-3 pr-4 text-gray-700">{formatDate(invoice.dateCreated)}</td>
                      <td className="py-3 pr-4">
                        <span className="font-medium text-gray-900">
                          #{invoice.stripeInvoiceId.slice(-8).toUpperCase()}
                        </span>
                        {invoice.status === 'open' && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-yellow-700">
                            <CircleDollarSign className="w-3 h-3" /> Pending payment
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-gray-900">{formatCurrency(invoice.amount)}</td>
                      <td className="py-3 pr-4">
                        <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex justify-end gap-2">
                          {invoice.status === 'open' && invoice.hostedInvoiceUrl && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(invoice.hostedInvoiceUrl!, '_blank')}
                              className="border-yellow-400 text-yellow-800 hover:bg-yellow-50"
                            >
                              Pay Now
                            </Button>
                          )}
                          {invoice.hostedInvoiceUrl && invoice.status !== 'open' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(invoice.hostedInvoiceUrl!, '_blank')}
                            >
                              View
                            </Button>
                          )}
                          {invoice.invoicePdf && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(invoice.invoicePdf!, '_blank')}
                            >
                              PDF
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )
              : renderEmpty()
          }
        </table>
      </div>

      {(invoices?.length ?? 0) > 0 && !isLoading && (
        <div className="mt-4">
          <Pagination
            total={invoices?.length || 0}
            limit={limit}
            offset={offset}
            onPageChange={setOffset}
            onLimitChange={(newLimit) => {
              setLimit(newLimit)
              setOffset(0)
            }}
          />
        </div>
      )}
    </div>
  )
}
